import { useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { UI_LOGICAL_SIZE } from "odyssey-shared";
import { Window } from "./frames.tsx";
import { cssPerLogical } from "./measure.ts";
import { logical } from "./metrics.ts";
import { windowRoom, type FramedWindowId } from "./place.ts";
import { useWindowFrame, type PlaceSource, type SizeSource } from "./window-frame.ts";
import { WindowFramedContext } from "./window-framed.ts";
import { keepFocusBeside, useModal, type ModalParts } from "./modal.ts";

/**
 * A modal window (ADR-061's amendment "Settings in sections, and nothing applies until Apply"): the kit's
 * `Window`, with its title bar, close button and footer, over a modal scrim that fills the nearest positioned
 * ancestor. It is a modal as the kit's `Dialog` is (`useModal`): the router's topmost layer, so Escape asks the
 * page to close it, holding the keyboard, keeping Tab and focus inside itself, focusing its first safe control as
 * it opens (the first in its body, then its footer, that is not a danger button, else its close button) and
 * giving focus back as it closes. The close button and Escape both only ask (`onClose`); while the page keeps the
 * window, it keeps its layer.
 *
 * The page says where the window sits and how large it is from the scrim's logical size (`fit`), measured as the
 * window is laid out and again whenever the scrim changes size, so the window never passes the page; until it
 * is measured, and under `react-dom/server`, the window is fitted to the 640 by 360 grid.
 *
 * It is the kit's system window (ADR-061's amendment "every game window moves and resizes", item 1): the window that
 * stands behind a scrim is framed as a play window is, by the id its page gives it. Its title bar moves it and its
 * edges, corners and grip resize it on the scrim's page, the scrim itself never moving; `fit` gives its default box and
 * the room it is held inside is the page's (`windowRoom`), so it is never off the page. Its title is a tab stop first
 * in the window and its grip the last, both inside the Tab trap, and it still focuses its first safe control as it
 * opens, never the title.
 */

/**
 * A window's box in its scrim, in whole logical pixels.
 */
export interface WindowBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * A modal window.
 *
 * @param props The `id`, which gives the window its frame and its limits (`WINDOW_LIMITS`); the `title`, which names the window; the body; the `footer`'s content when it has one;
 *   `onClose`, which the close button and Escape call to ask the page to take the window away, and which the
 *   page may refuse by keeping it mounted; `fit`, which gives the window's default box for the scrim's logical size; and
 *   `place` and `size`, where the page keeps the window's place and size (a visit's memory where it gives none).
 * @returns The scrim holding the window.
 * @throws Error when mounted outside a `KitInputProvider`.
 * @throws RangeError when `fit` gives a box that is not on whole logical pixels.
 * @example
 * <ModalWindow id="settings" title="Settings" onClose={close} fit={(area) => ({ left: 14, top: 44, width: area.width - 28, height: 236 })}>
 *   <p>Rows.</p>
 * </ModalWindow>
 */
export function ModalWindow({ id, title, children, footer, onClose, fit, place, size }: { readonly id: FramedWindowId; readonly title: string; readonly children: ReactNode; readonly footer?: ReactNode; readonly onClose: () => void; readonly fit: (area: { readonly width: number; readonly height: number }) => WindowBox; readonly place?: PlaceSource | undefined; readonly size?: SizeSource | undefined }): ReactElement {
  const scrim = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [area, setArea] = useState<{ readonly width: number; readonly height: number }>(UI_LOGICAL_SIZE);

  /**
   * The window's parts as its first focus reads them.
   *
   * @returns Its body and footer, and its close button.
   */
  const parts = (): ModalParts => {
    const element = box.current;
    if (element === null) {
      return { content: [], close: null };
    }
    const content = [element.querySelector<HTMLElement>("[data-kit='window-body']"), element.querySelector<HTMLElement>("[data-kit='window-footer']")].filter((part): part is HTMLElement => part !== null);
    return { content, close: element.querySelector<HTMLElement>("[data-kit='window-header'] button") };
  };
  const { trap } = useModal(box, parts, onClose);

  useLayoutEffect(() => {
    const outer = scrim.current;
    if (outer === null) {
      return undefined;
    }
    /**
     * Takes the scrim's logical size, while it has one under the overlay root. The window is watched as well as
     * the scrim: when the logical pixel changes size under an unchanged page, as a change of render resolution
     * makes it (ADR-093 decision 10), the scrim keeps its CSS size and only the window's moves.
     */
    const measure = (): void => {
      const px = cssPerLogical(outer);
      const rect = outer.getBoundingClientRect();
      if (px === null || rect.width <= 0 || rect.height <= 0) {
        return;
      }
      const next = { width: rect.width / px, height: rect.height / px };
      setArea((current) => (current.width === next.width && current.height === next.height ? current : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(outer);
    if (box.current !== null) {
      observer.observe(box.current);
    }
    return () => observer.disconnect();
  }, []);

  const { box: at, frame } = useWindowFrame({ id, place, size, home: fit(area), room: windowRoom(area) });
  return (
    <div ref={scrim} className="ot-scrim" data-kit="scrim" onMouseDown={keepFocusBeside}>
      <div ref={box} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} className="ot-modal-window" data-kit="modal-window" onKeyDown={trap} style={{ left: logical(at.left), top: logical(at.top) }}>
        <Window title={title} width={at.width} height={at.height} onClose={onClose} footer={footer} frame={frame}>
          <WindowFramedContext.Provider value={frame !== null}>{children}</WindowFramedContext.Provider>
        </Window>
      </div>
    </div>
  );
}
